import BusinessDateFormats from '@/modules/common/filters/BusinessDateFormats';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Formats date to ISO format YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date in YYYY-MM-DD format
 */
export const formatToISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Safely parses a 'YYYY-MM-DD' string into a Date object at noon.
 * This avoids timezone shifts caused by `new Date('YYYY-MM-DD')` which interprets the date as UTC.
 * @param {string|Date} isoDateString - Date string in 'YYYY-MM-DD' format or Date object.
 * @returns {Date | null} - A Date object or null if input is falsy.
 */
export const parseISODateString = (isoDateString) => {
  if (!isoDateString) return null;
  if (isoDateString instanceof Date) return isoDateString;
  return new Date(`${isoDateString}T00:00:00`);
};

/**
 * Gets today's date in YYYY-MM-DD format, respecting the user's timezone
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const formatTodayToISODate = () => {
  const today = new Date();
  return formatToISODate(today);
};

/**
 * Calculates the end date of a package based on validity parameters
 * @param {string} validFrom - Starting date in YYYY-MM-DD format
 * @param {number} expiration - Duration number
 * @param {string} expirationUnit - Duration unit (d: days, w: weeks, m: months, y: years)
 * @returns {string} - End date in YYYY-MM-DD format
 */
export const addDurationToDate = (validFrom, expiration, expirationUnit) => {
  const unitKey = expirationUnit;

  const unitsMapping = {
    d: 'days',
    w: 'weeks',
    m: 'months',
    y: 'years',
  };
  const unit = unitsMapping[unitKey];
  if (!unit) return null;

  const date = new Date(validFrom);

  switch (unit) {
    case 'days':
      date.setDate(date.getDate() + expiration);
      break;
    case 'weeks':
      date.setDate(date.getDate() + (expiration * 7));
      break;
    case 'months':
      date.setMonth(date.getMonth() + expiration);
      date.setDate(date.getDate() - 1);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() + expiration);
      date.setDate(date.getDate() - 1);
      break;
    default:
      return null;
  }

  return formatToISODate(date);
};

/**
 * Calculates the end date of a package based on validity parameters (alias for addDurationToDate)
 * @param {string} validFrom - Starting date in YYYY-MM-DD format
 * @param {number} expiration - Duration number
 * @param {string} expirationUnit - Duration unit (d: days, w: weeks, m: months, y: years)
 * @returns {string} - End date in YYYY-MM-DD format
 */
export const calculateValidUntil = (validFrom, expiration, expirationUnit) => addDurationToDate(validFrom, expiration, expirationUnit);

/**
 * Returns today's date if the provided date is before today, otherwise returns the provided date
 * @param {string} date - Date to check in YYYY-MM-DD format
 * @returns {string} - Today's date or the provided date in YYYY-MM-DD format
 */
export const todayIfBefore = (date) => {
  const today = formatTodayToISODate();
  if (!date) return today;

  const inputDate = parseISODateString(date);
  const todayDate = parseISODateString(today);

  if (!inputDate || !todayDate) return today;

  return inputDate < todayDate ? today : date;
};

/**
 * Formats a date in "day month year" format, always showing the year
 * @param {string|Date} date - Date to format
 * @param {boolean} isFullMonth - Whether to use full month name
 * @returns {string} - Formatted date in "day month year" format
 */
export const formatDateWithYear = (date, isFullMonth = true) => {
  if (!date) return '';

  // Convert to Date object if it's a string
  let dateObj;
  if (typeof date === 'string') {
    // If it's already an ISO string with time, use it directly
    if (date.includes('T') || date.includes('Z')) {
      dateObj = new Date(date);
    } else {
      // If it's YYYY-MM-DD, convert to ISO string for BusinessDateFormats
      dateObj = new Date(`${date}T00:00:00`);
    }
  } else {
    dateObj = date;
  }

  if (Number.isNaN(dateObj.getTime())) return '';

  return BusinessDateFormats.dateWithYear(dateObj.toISOString(), isFullMonth);
};

/**
 * Formats a date showing month and day for current year, or month, day, and year for other years
 * @param {string|Date} date - Date to format
 * @param {boolean} isFullMonth - Whether to use full month name
 * @returns {string} - Formatted date (e.g., "Jul 15" for current year, "Jul 15, 2022" for other years)
 */
export const formatDateNoCurrentYear = (date, isFullMonth = false) => {
  if (!date) return '';

  // Convert to Date object if it's a string
  let dateObj;
  if (typeof date === 'string') {
    // If it's already an ISO string with time, use it directly
    if (date.includes('T') || date.includes('Z')) {
      dateObj = new Date(date);
    } else {
      // If it's YYYY-MM-DD, convert to ISO string for BusinessDateFormats
      dateObj = new Date(`${date}T00:00:00`);
    }
  } else {
    dateObj = date;
  }

  if (Number.isNaN(dateObj.getTime())) return '';

  return BusinessDateFormats.dateNoCurrentYear(dateObj.toISOString(), isFullMonth);
};

/**
 * Gets the start of the current week
 * @param {number} startOfWeek - The day the week starts (0 for Sunday, 1 for Monday, etc.)
 * @returns {string} - The start of the week
 */
export const getStartOfWeek = (weekStartsOn = 0) => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const diff = (dayOfWeek - weekStartsOn + 7) % 7;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - diff);
  return start;
};

/**
 * @returns {string} - The start of the month
 */
export const getStartOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
};

/**
 * @returns {string} - The start of the year
 */
export const getStartOfYear = () => {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
};

/**
 * Formats a date with long month name, day, and year (e.g., "October 15, 2023")
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale string (e.g., 'en-US', 'fr-FR')
 * @returns {string} - Formatted date or '-' if invalid
 */
export const formatLongDate = (date, locale = 'en-US') => {
  if (!date) {
    return '';
  }

  try {
    const dateObj = new Date(date);
    if (Number.isNaN(dateObj.getTime())) {
      return '';
    }

    const options = {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    };

    return dateObj.toLocaleDateString(locale, options);
  } catch (error) {
    return '';
  }
};
